<template>
    <div>
        <hts-data-table
            ref="dataTables"
            :headers="headers"
            :server="server"
            use-selection
            :use-archive="useTripArchive"
            store-name="tripDataTable"
            store-key="Trip" />

        <add-trip
            v-if="checkPermission(layoutPermissions.trip_management)"
            ref="addTrip"
            @refreshTables="refreshTables" />

        <edit-trip
            v-if="checkPermission(layoutPermissions.trip_management)"
            ref="editTrip"
            @refreshTables="refreshTables" />

        <detail-trip
            v-if="checkPermission(layoutPermissions.trip_management)"
            ref="detailTrip"
            @refreshTables="refreshTables" />

        <cancel
            v-if="checkPermission(layoutPermissions.trip_management)"
            ref="cancel"
            @refreshTables="refreshTables" />
        <hts-modal
            v-if="checkPermission(layoutPermissions.order_delete)"
            :show="showPopupDelete"
            :title="$t('labels.deleteTrip')"
            type="confirm"
            :content="$t('labels.tripConfirmDelete')"
            @close="closePopupDelete"
            @confirm="() => actionDelete()" />

        <trip-info
            ref="tripInfoComponent"
            :api="$urlPartner.trip.trip.detail" />
    </div>
</template>

<script setup lang="ts">
import TripInfo from '../-components/trip-info.vue'
import AddTrip from './ignore/add.vue'
import Cancel from './ignore/cancel.vue'
import Deadline from './ignore/component/deadline.vue'
import Request from './ignore/component/request.vue'
import Route from './ignore/component/route.vue'
import DetailTrip from './ignore/detail.vue'
import EditTrip from './ignore/edit.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
import Status from '~/components/status.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

const { $formatters } = useNuxtApp()

const server = $urlPartner.trip.trip['get-list']

const dataTables = ref()
const addTrip = ref()
const editTrip = ref()
const detailTrip = ref()
const cancel = ref()
const showPopupDelete = ref(false)
const selectedForDelete: { [key: string]: any } | null = ref({})

const showAdd = () => {
  if (addTrip.value) {
    addTrip.value.open()
  }
}

const showEdit = (data: any) => {
  if (editTrip.value) {
    editTrip.value.open(data._id)
  }
}

const handleViewDetail = (data: any) => {
  if (detailTrip.value) {
    detailTrip.value.open(data._id)
  }
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const handleDelete = (data: Object) => {
  selectedForDelete.value = data
  showPopupDelete.value = true
}

const handleCancel = (data: any) => {
  if (cancel.value) {
    cancel.value.open(data._id)
  }
}

const checkShowCancel = (data: any) => {
  const status = data.status

  if (typeof status !== 'number') {
    return false
  }

  if ([-1].includes(status)) {
    return false
  }

  return true
}

const tripInfoComponent: any = ref()
const openView = (data: any) => {
  tripInfoComponent.value && tripInfoComponent.value.show(data._id)
}
onMounted(() => {
  const route = useRoute()
  if (route.query.id) {
    openView({ _id: route.query.id })
  }
})
const headers = ref([
  {
    field: 'code',
    headerName: 'Mã chuyến',
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  // {
  //   field: 'tasks',
  //   headerName: 'Mã chặng hàng',
  //   cellRenderer: JourneyCell,
  //   autoHeight: true,
  //   minWidth: 150
  // },
  {
    field: 'containerNumbers',

    headerName: $t('labels.containerNo'),
    valueGetter: (params: any) => {
      const { containerNumbers } = params.data || {}

      if (!containerNumbers) {
        return
      }

      if (!Array.isArray(containerNumbers)) {
        return ''
      }

      const a = containerNumbers.map((item: any) => item.containerNumber)
      return a.join(', ')
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.containerType'),
    valueGetter: (params: any) => {
      const { contCategory, goodsStatusName } = params.data?.orderData || {}
      let text = ''
      if (!contCategory) {
        return
      }

      if (contCategory === 20) {
        text = $t('labels.20f')
      } else if (contCategory === 40) {
        text = $t('labels.40f')
      } else if (contCategory === 45) {
        text = $t('labels.45f')
      }

      text += ` - ${goodsStatusName || ''}`
      return text
    }
  },
  {
    field: 'vehicleLicensePlate',
    headerName: 'Phương tiện kéo',
    // filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      const vehicleLicensePlate = params.data?.vehicleLicensePlate
      const vehicleTempLicensePlate = params.data?.vehicleTempLicensePlate
      return vehicleLicensePlate || vehicleTempLicensePlate
    }
  },
  {
    field: 'workflowName',
    headerName: $t('labels.workflow'),
    valueGetter: (params: any) => {
      return params.data?.orderData?.workflowName
    }
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    minWidth: 700,
    filter: 'agTextColumnFilter',
    cellRenderer: Route
  },
  {
    field: 'deadline',
    headerName: $t('labels.deadline'),
    autoHeight: true,
    cellRenderer: Deadline
  },
  {
    field: 'request',
    headerName: $t('labels.note'),
    cellRenderer: Request
  },
  {
    field: 'orderCode',
    headerName: $t('labels.orderCode'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'journeyCount',
    headerName: $t('labels.journey3'),
    valueGetter: (params: any) => {
      const journeyCount = params.data?.journeyCount
      if (!journeyCount) {
        return ''
      }

      if (journeyCount === 1) {
        return $t('labels.false')
      }

      return $t('labels.true')
    }
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data?.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'moderationStatus',
    headerName: $t('labels.moderationStatus'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'tripModerationStatus',
      label: '',
      keyStatus: 'moderationStatus'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      {
        name: $t('labels.all'),
        value: null
      },
      {
        name: 'Chờ xác nhận',
        value: 1,
        class: { '!bg-yellow-100': true, '!text-yellow-800': true } // Màu vàng (#FFEB3B)
      },
      {
        name: 'Chờ điều đội xe',
        value: 5,
        class: { '!bg-sky-100': true, '!text-sky-800': true } // Màu xanh nhạt (#81D4FA)
      },
      {
        name: 'Chờ điều xe',
        value: 2,
        class: { '!bg-blue-100': true, '!text-blue-800': true } // Màu xanh dương (#2196F3)
      },
      {
        name: 'Có xe dự kiến',
        value: 3,
        class: { '!bg-orange-100': true, '!text-orange-800': true } // Màu cam (#FF9800)
      },
      {
        name: 'Có xe chính thức',
        value: 4,
        class: { '!bg-green-100': true, '!text-green-800': true } // Màu xanh lá cây (#4CAF50)
      },
      {
        name: $t('labels.cancel'),
        value: -1,
        class: { '!bg-error-100': true, '!text-error-800': true } // Màu đỏ (#F44336)
      }
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'status',
    headerName: $t('labels.shippingStatus'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'tripStatus',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: 'Chưa thực hiện', value: 1, class: { '!bg-gray-100': true, '!text-gray-800': true } }, // Chưa thực hiện: Màu xám
      { name: 'Đang thực hiện', value: 2, class: { '!bg-blue-100': true, '!text-blue-800': true } }, // Đang thực hiện: Màu xanh dương nhạt
      { name: 'Hoàn thành', value: 3, class: { '!bg-green-100': true, '!text-green-800': true } }, // Hoàn thành: Màu xanh lá cây
      { name: $t('labels.cancel'), value: -1, class: { '!bg-error-100': true, '!text-error-800': true } } // Hủy: Màu đỏ
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'mergeContainer',
    headerName: 'Yêu cầu đặc biệt',
    valueGetter: (params: any) => {
      const results = [] as string[]
      if (params?.data?.orderData?.mergeContainer) {
        results.push($t('labels.mergeContainer'))
      }

      if (params?.data?.orderData?.headContainer) {
        results.push($t('labels.headContainer'))
      }

      return results.join(', ')
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'tripDataTable',
      storeName: 'Trip',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          click: openView
        }
        // {
        //   icon: 'hi-x-circle-icon',
        //   label: $t('labels.cancel'),
        //   class: 'text-error-500 hover:text-error-500',
        //   permissions: checkPermission(layoutPermissions.trip_management),
        //   validate: checkShowCancel,
        //   isAll: true,
        //   click: handleCancel
        // }
      ]
    }
  }
])

const useTripArchive = { api: $urlPartner.trip.trip.archive, permissions: checkPermission(layoutPermissions.trip_management) }

const closePopupDelete = () => {
  selectedForDelete.value = {}
  showPopupDelete.value = false
}

const actionDelete = () => {
  const api = $urlPartner.trip.trip.delete

  $api(api, { body: { ids: [selectedForDelete.value?._id || null] } }).then((data: any) => {
    const resData: any = data?.data?.value
    if (resData?.success) {
      closePopupDelete()
      refreshTables()
      process.client && $toast().success(resData.data)
    }
  })
}
</script>
